import React from "react";
import { connect } from "react-redux";
import {
  serviceCaller,
  ServiceCallerProps,
  withLoginRedirect,
} from "../../../composition";
import { Col, Row, Progress, FormGroup, Button } from "reactstrap";
import firebase from "firebase/compat/app";
import "firebase/compat/database";
import s from "./styles.scss";
import withStyles from "isomorphic-style-loader/withStyles";
import { firebaseConnect, isEmpty, isLoaded } from "react-redux-firebase";
import Files from "react-files";
import * as commonActions from "../../../../utils/actions/common";

import ICharity from "../../../../types/services/ICharity";
import { IUploadProgress } from "../../../../types/IUploadProgress";
import classNames from "classnames";
import { queueFileForProcessing } from "../../../../utils/fetch";
import {
  FileProcessingRequest,
  FileProcessingRequestStatus,
  FileProcessingType,
  Segment,
  SelectOption,
  inFlightFileRequestStatuses,
} from "../types/FileProcessingRequest.type";

import { Card, CardBody, CardTitle, CardText } from "reactstrap";
import CreatableSelect from "react-select/creatable";
import get from "lodash.get";
import ReactTooltip from "react-tooltip";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import CloudUploadOutlined from "@mui/icons-material/CloudUploadOutlined";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import StyledStepper, { StepItem } from "../../../../toolkit/stepper";
import ModalFrame from "../../../../toolkit/modalFrame";
import breadcrumbStyles from "../../charitySetup/onboardingSteps/common/WizardBreadcrumb/styles.scss";

interface Props extends ServiceCallerProps {
  currentCharity: ICharity;
  email: string;
  name: string;
  firebase: any;
  userId: string;
  hideSelection: () => void;
  refreshDashboard: () => void;
  segmentsOptions: SelectOption[];
}

type FileType = "ReplaceFile" | "AppendFile";
type StepType = "SelectFileType" | "AddSegment" | "uploadFile";

const MAX_FILE_SIZE_BYTES = 10000000;

const formatMegabytes = (bytes: number) =>
  `${Number((bytes / 1000000).toFixed(1))} MB`;

const STEP_ORDER: StepType[] = ["SelectFileType", "AddSegment", "uploadFile"];
const REPLACE_FILE_SEGMENTS_INFO =
  "If a contact is in one of this file's segments but not in the file, they'll be taken out of that segment.";

const STEPS: StepItem[] = [
  { label: "Select upload mode", icon: <TuneOutlined fontSize="small" /> },
  { label: "Select segment", icon: <GroupsOutlined fontSize="small" /> },
  { label: "Upload file", icon: <CloudUploadOutlined fontSize="small" /> },
];

function hasInFlightFileRequestFromData(fileRequestsData: unknown): boolean {
  if (!fileRequestsData || typeof fileRequestsData !== "object") {
    return false;
  }
  return Object.keys(fileRequestsData).some(requestId => {
    const request = (fileRequestsData as Record<string, FileProcessingRequest>)[
      requestId
    ];
    return (
      request &&
      inFlightFileRequestStatuses.indexOf(request.status) !== -1
    );
  });
}

interface State {
  fileUpload?: IUploadProgress;
  fileUrl?: string;
  currentFile?: any;
  showErrors: boolean;
  message: string;
  busyLoad: boolean;
  fileType?: FileType;
  isNewSegment: boolean;
  replaceFileSegments: boolean;
  selectedSegment: SelectOption;
  currentStep: StepType;
  showDuplicatesModal: boolean;
  hasInFlightFileRequest: boolean;
}

class ImportFile extends React.Component<Props, State> {
  fileRequestsRef: firebase.database.Reference | null = null;
  fileRequestsCallback:
    | ((snapshot: firebase.database.DataSnapshot) => void)
    | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      message: null,
      showErrors: false,
      busyLoad: false,
      fileType: null,
      isNewSegment: false,
      replaceFileSegments: false,
      selectedSegment: null,
      currentStep: "SelectFileType",
      showDuplicatesModal: false,
      hasInFlightFileRequest: false,
    };
  }

  componentDidMount() {
    this.props.hideSelection();
    this.attachFileRequestsListener(this.props.currentCharity?.id);
  }

  componentWillUnmount() {
    this.detachFileRequestsListener();
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.currentCharity.id !== prevProps.currentCharity.id) {
      this.attachFileRequestsListener(this.props.currentCharity.id);
    }
  }

  attachFileRequestsListener = (charityId?: string) => {
    this.detachFileRequestsListener();
    if (!charityId) {
      this.setState({ hasInFlightFileRequest: false });
      return;
    }

    this.fileRequestsRef = firebase.database().ref(`${charityId}/fileRequests`);
    this.fileRequestsCallback = snapshot => {
      const fileRequestsData = snapshot.val();
      this.setState({
        hasInFlightFileRequest: hasInFlightFileRequestFromData(
          fileRequestsData
        ),
      });
    };
    this.fileRequestsRef.on("value", this.fileRequestsCallback);
  };

  detachFileRequestsListener = () => {
    if (this.fileRequestsRef && this.fileRequestsCallback) {
      this.fileRequestsRef.off("value", this.fileRequestsCallback);
    }
    this.fileRequestsRef = null;
    this.fileRequestsCallback = null;
  };

  toggleDuplicatesModal = () => {
    this.setState(prev => ({ showDuplicatesModal: !prev.showDuplicatesModal }));
  };

  isUploading = () => {
    return this.state.fileUpload != null && !this.state.fileUrl;
  };

  goBack = () => {
    this.props.navigate(-1);
  };

  onClickCard = (action: FileType) => {
    if (action === "ReplaceFile") {
      this.setState({
        fileType: action,
        currentStep: "uploadFile",
        selectedSegment: null,
        isNewSegment: false,
        replaceFileSegments: false,
      });
      return;
    }

    this.setState({
      fileType: action,
      currentStep: "AddSegment",
    });
  };

  calculateUploadProgress = () => {
    if (!this.state.fileUpload) {
      return 0;
    } else {
      let totalBytes = this.state.fileUpload.totalBytes;
      let currentBytes = this.state.fileUpload.currentBytes;
      return 100 * (currentBytes / totalBytes);
    }
  };

  matchExtensionToContentType = (fileExtension: string) => {
    switch (fileExtension) {
      case "csv":
        return "text/csv";
      case "xls":
        return "application/vnd.ms-excel";
      case "xlsx":
        return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    }
    return null;
  };

  uploadFile = () => {
    if (
      this.state.busyLoad ||
      this.state.fileUpload != null ||
      this.state.hasInFlightFileRequest
    ) {
      return;
    }

    const { currentFile } = this.state;
    const folderName = "import-files";
    const currentTimestamp = Date.now();

    const charityId = this.props.currentCharity.id;

    if (currentFile) {
      this.setState({ busyLoad: true });

      const fileRequestNode = this.props.firebase
        .database()
        .ref()
        .child(charityId)
        .child("fileRequests");

      const fileRequestRef = fileRequestNode.push().key;
      const storageRef = this.props.firebase.storage().ref();

      const fileExtension = currentFile.extension;
      const fileSizeReadable = currentFile.sizeReadable;
      const fileSize = currentFile.size;
      const fileName = currentFile.name.replace(`.${fileExtension}`, "");
      const filePath = `${charityId}/${folderName}/${fileRequestRef}/${currentTimestamp}.${fileExtension}`;
      const fileRef = storageRef.child(filePath);

      const metadata = {
        customMetadata: {
          user: this.props.name ? this.props.name : this.props.email,
          email: this.props.email ? this.props.email : null,
          size: fileSizeReadable,
          userId: this.props.userId ? this.props.userId : null,
        },
      };

      const fileUpload = fileRef.put(currentFile, metadata);
      fileUpload.on(
        "state_changed",
        snapshot => {
          this.setState({
            fileUpload: {
              totalBytes: snapshot.totalBytes,
              currentBytes: snapshot.bytesTransferred,
            },
          });
        },
        error => {
          console.error("Error while uploading file", error);
          this.setState({ busyLoad: false, fileUpload: null });
        },
        () => {
          fileUpload.snapshot.ref.getDownloadURL().then((url: string) => {
            let segmentName = null;

            if (this.state.selectedSegment)
              segmentName = this.state.selectedSegment.value;

            this.setState(
              {
                fileUrl: url,
              },
              () => {
                const request: FileProcessingRequest = {
                  fileName: fileName,
                  fileExtension: fileExtension,
                  fileSize: fileSize,
                  filePath: filePath,
                  fileUrl: url,
                  mimeType: this.matchExtensionToContentType(fileExtension),
                  bucketName: storageRef.bucket,
                  type: FileProcessingType.DONORS_UPLOAD,
                  status: FileProcessingRequestStatus.RECEIVING,
                  uploadedBy: {
                    userId: this.props.userId ? this.props.userId : null,
                    userName: this.props.name
                      ? this.props.name
                      : this.props.email,
                    userEmail: this.props.email ? this.props.email : null,
                  },
                  uploadedDateTimestamp: currentTimestamp,
                  metadata: {
                    fileType: this.state.fileType,
                    isNewSegment: this.state.isNewSegment,
                    replaceFileSegments:
                      this.state.fileType === "AppendFile" &&
                      this.state.replaceFileSegments,
                    segmentName: segmentName,
                  },
                };

                fileRequestNode
                  .child(fileRequestRef)
                  .update(request)
                  .then(() => {
                    queueFileForProcessing(
                      this.props.config.adminServiceUrl,
                      this.props.currentCharity.id,
                      fileRequestRef,
                      this.props.serviceAccessToken
                    ).then(() => {
                      this.showSuccessMessage();
                    });
                  });
              }
            );
          });
        }
      );
    } else {
      alert("Invalid file");
    }
  };

  onFilesChange = file => {
    const currentFile = file.length > 0 ? file[file.length - 1] : null;

    if (currentFile) {
      this.setState({
        currentFile: currentFile,
        showErrors: false,
        message: null,
      });
    } else {
      this.setState(prev => ({
        currentFile: null,
        showErrors: true,
        message: prev.showErrors
          ? prev.message
          : "Select an XLSX or CSV file to upload.",
      }));
    }
  };

  onError = (error, file) => {
    let message = error.message;
    if (error.code === 1) {
      message = `${file.name} is not a supported file type. Upload an XLSX or CSV file.`;
    } else if (error.code === 2) {
      message = `This file is ${formatMegabytes(file.size)}. Maximum file size is ${formatMegabytes(MAX_FILE_SIZE_BYTES)}.`;
    }
    this.setState({
      showErrors: true,
      message,
      busyLoad: false,
    });
  };

  onTypeChange = event => {
    this.setState({ fileType: event.target.value });
  };

  showSuccessMessage = () => {
    setTimeout(() => {
      alert("Upload Successful");
      this.props.navigate(-1);
    }, 1000);
  };

  goToPreviousStep = () => {
    const skipsSegment = this.state.fileType === "ReplaceFile";
    const backPage: StepType =
      this.state.currentStep === "uploadFile" && !skipsSegment
        ? "AddSegment"
        : "SelectFileType";

    this.setState({
      currentStep: backPage,
      fileType: backPage === "SelectFileType" ? null : this.state.fileType,
    });
  };

  onCardKeyDown = (event: React.KeyboardEvent, action: FileType) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.onClickCard(action);
    }
  };

  renderStepperBar = () => {
    const backButton = (
      <button
        type="button"
        onClick={this.goBack}
        className={breadcrumbStyles.backButton}
        aria-label="Back"
      >
        <i className="fas fa-arrow-left" aria-hidden="true" />
      </button>
    );

    return (
      <div className={s.stepperBar}>
        <div className={s.stepperHeaderRow}>
          <div className={s.backSlot}>{backButton}</div>
          <div className={s.stepperWrap}>
            <StyledStepper
              steps={STEPS.map((step, index) => ({
                ...step,
                skipped: index === 1 && this.state.fileType === "ReplaceFile",
              }))}
              activeStep={STEP_ORDER.indexOf(this.state.currentStep)}
              onStepChange={() => {}}
            />
          </div>
          <div className={s.backSlot} aria-hidden="true">
            <span
              className={classNames(breadcrumbStyles.backButton, s.backMirror)}
            >
              <i className="fas fa-arrow-left" aria-hidden="true" />
            </span>
          </div>
        </div>
      </div>
    );
  };

  renderStepFooter = () => {
    const isUploadStep = this.state.currentStep === "uploadFile";
    const uploadInProgress =
      this.state.busyLoad || this.state.fileUpload != null;
    const importLocked = this.state.hasInFlightFileRequest;
    const lockTooltipId = "import-file-upload-locked";

    const primaryDisabled = isUploadStep
      ? !this.state.currentFile || uploadInProgress || importLocked
      : !this.state.fileType ||
        (this.state.isNewSegment &&
          this.state.selectedSegment.value.length == 0);

    const onPrimary = isUploadStep
      ? this.uploadFile
      : () => this.setState({ currentStep: "uploadFile" });

    const primaryButton = (
      <Button
        color="primary"
        className={s.footerPrimary}
        onClick={onPrimary}
        disabled={primaryDisabled}
      >
        {isUploadStep && uploadInProgress
          ? "Uploading…"
          : isUploadStep
            ? "Upload File"
            : "Next"}
      </Button>
    );

    return (
      <div className={s.footerBar}>
        <Button
          color="link"
          className={s.footerBack}
          onClick={this.goToPreviousStep}
          disabled={isUploadStep && uploadInProgress}
        >
          Back
        </Button>
        {isUploadStep && importLocked ? (
          <span data-tip data-for={lockTooltipId}>
            {primaryButton}
            <ReactTooltip
              id={lockTooltipId}
              type="info"
              place="top"
              effect="float"
            >
              Wait for the current file to finish processing before you upload a
              new file
            </ReactTooltip>
          </span>
        ) : (
          primaryButton
        )}
      </div>
    );
  };

  renderDuplicatesModal = () => {
    return (
      <ModalFrame
        title="Duplicates Found Automatically"
        isOpen={this.state.showDuplicatesModal}
        toggle={this.toggleDuplicatesModal}
        size="lg"
        centered
      >
        <p className={s.modalText}>
          ViewSpark will know if a contact or company already exists. This is
          done by the contact email address, phone number and contact id.
        </p>
        <p className={s.modalText}>
          If you import a contact or company that already exists in your
          database, any matching properties will be updated with the latest data
          from your import.
        </p>
        <p className={s.modalText}>
          If you don&apos;t want to overwrite existing data in your database,
          remove those columns from your file before you import.
        </p>
      </ModalFrame>
    );
  };

  render() {
    return (
      <main className={s.main}>
        <div className={s.container}>
          {this.renderStepperBar()}

          <div className={s.stepContent}>
            {this.state.currentStep == "SelectFileType" && (
              <div className={s.stepInner}>
                <Card
                  className={classNames(s.vsCard, s.primaryCard)}
                  role="button"
                  tabIndex={0}
                  onClick={() => this.onClickCard("AppendFile")}
                  onKeyDown={e => this.onCardKeyDown(e, "AppendFile")}
                >
                  <CardBody className={s.primaryCardBody}>
                    <i
                      className={classNames("fa fa-file", s.primaryCardIcon)}
                      aria-hidden="true"
                    />
                    <CardTitle tag="h5" className={s.primaryCardTitle}>
                      Upload New File
                    </CardTitle>
                    <CardText className={s.primaryCardText}>
                      Supplemental data that will be appended to your existing
                      database.
                    </CardText>
                  </CardBody>
                </Card>

                <div className={s.orDivider}>or</div>

                <Card
                  className={classNames(s.vsCard, s.secondaryCard)}
                  role="button"
                  tabIndex={0}
                  onClick={() => this.onClickCard("ReplaceFile")}
                  onKeyDown={e => this.onCardKeyDown(e, "ReplaceFile")}
                >
                  <CardBody className={s.secondaryCardBody}>
                    <i
                      className={classNames(
                        "fa fa-refresh",
                        s.secondaryCardIcon
                      )}
                      aria-hidden="true"
                    />
                    <div>
                      <CardTitle tag="h6" className={s.secondaryCardTitle}>
                        Replace Entire Database
                      </CardTitle>
                      <CardText className={s.secondaryCardText}>
                        Total replacement of your donors, segments, keywords and
                        invalid donors.
                      </CardText>
                    </div>
                  </CardBody>
                </Card>

                <a
                  href="https://storage.googleapis.com/public-viewspark-production/Viewspark%20Donor%20Import%20Sample%20File.xlsx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={s.sampleDownload}
                >
                  <i
                    className={classNames("fa fa-download", s.sampleDownloadIcon)}
                    aria-hidden="true"
                  />
                  Download Sample Spreadsheet
                </a>

                <hr className={s.helpDivider} />

                <h3 className={s.helpTitle}>Need help getting started?</h3>

                <div className={s.helpLinks}>
                  <a
                    href="https://www.viewspark.org/importguide"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={s.uploadFileLink}
                  >
                    View Import Guide
                    <span className={s.linkIcon}>
                      <i className="fa fa-external-link" aria-hidden="true"></i>
                    </span>
                  </a>
                  <a
                    href="https://www.viewspark.org/importguide#:~:text=Frequently%20Asked%20Question"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={s.uploadFileLink}
                  >
                    View The FAQ
                    <span className={s.linkIcon}>
                      <i className="fa fa-external-link" aria-hidden="true"></i>
                    </span>
                  </a>
                </div>
              </div>
            )}

            {this.state.currentStep == "AddSegment" && (
              <div className={s.stepInner}>
                <p className={s.stepIntro}>
                  Enter a new segment name or choose an existing one.
                </p>

                <FormGroup>
                  <CreatableSelect
                    id={"segmentName"}
                    name={"segmentName"}
                    placeholder={"Select segment"}
                    value={this.state.selectedSegment}
                    onChange={(e: SelectOption) => {
                      this.setState({
                        selectedSegment: e,
                        isNewSegment:
                          e &&
                          !this.props.segmentsOptions
                            .map(option => option.value)
                            .includes(e.value),
                      });
                    }}
                    options={this.props.segmentsOptions}
                    className={s.segmentSelect}
                    isClearable
                  />
                  <p className={s.fieldHelper}>
                    Adding a segment is optional. Click Next to skip this step
                    and add all of your contacts to your database without
                    appending them to a specific segment.
                  </p>
                </FormGroup>

                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={this.state.replaceFileSegments}
                        onChange={(_, checked) =>
                          this.setState({ replaceFileSegments: checked })
                        }
                        color="primary"
                      />
                    }
                    label={
                      <>
                        Replace segments on the file{" "}
                        <i
                          className="fas fa-info-circle"
                          data-tip
                          data-for="replace-file-segments-info"
                          tabIndex={0}
                          aria-label={REPLACE_FILE_SEGMENTS_INFO}
                          onClick={e => e.preventDefault()}
                        />
                      </>
                    }
                  />
                  <ReactTooltip
                    id="replace-file-segments-info"
                    type="info"
                    place="top"
                    effect="solid"
                  >
                    {REPLACE_FILE_SEGMENTS_INFO}
                  </ReactTooltip>
                </FormGroup>
              </div>
            )}

            {this.state.currentStep == "uploadFile" && (
              <div className={s.stepInner}>
                <div className={s.uploadIntro}>
                  <p>
                    Before you upload your files below, make sure your file
                    is&nbsp;
                    <a
                      href="https://www.viewspark.org/importguide"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className={s.uploadFileLink}>
                        ready to be imported
                      </span>
                      <span className={s.linkIcon}>
                        <i
                          className="fa fa-external-link"
                          aria-hidden="true"
                        ></i>
                      </span>
                    </a>
                  </p>
                  <p>
                    Learn how ViewSpark knows when your records are{" "}
                    <button
                      type="button"
                      className={classNames(s.linkButton, s.uploadFileLink)}
                      onClick={this.toggleDuplicatesModal}
                    >
                      duplicates
                    </button>
                  </p>
                </div>

                <div>
                  <Files
                    className={
                      !this.state.currentFile ? s.dropzone : s.filledDropzone
                    }
                    onChange={this.onFilesChange}
                    onError={this.onError}
                    accepts={[
                      "text/csv",
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    ]}
                    maxFileSize={MAX_FILE_SIZE_BYTES}
                    multiple={false}
                    minFileSize={0}
                    clickable
                    dropActiveClassName={s["files-dropzone-active"]}
                  >
                    {this.state.currentFile && (
                      <div>
                        <Row>
                          <Col
                            md={{ size: 12 }}
                            className="d-flex align-items-center"
                          >
                            <span
                              style={{
                                color: "#1fa67a",
                                fontSize: "40px",
                                marginRight: "7px",
                              }}
                            >
                              <i className="fa fa-table" aria-hidden="true"></i>
                            </span>
                            <span style={{ fontSize: "18px" }}>
                              {this.state.currentFile.name}
                            </span>
                          </Col>
                        </Row>
                      </div>
                    )}

                    {!this.state.currentFile && (
                      <div>
                        <i
                          className={classNames("fa fa-upload", s.dropzoneIcon)}
                          aria-hidden="true"
                        />
                        <span style={{ fontSize: "16px" }}>
                          Click here to select your donor file, or drop it here
                        </span>
                        <br />
                        <span style={{ fontSize: "16px" }}>
                          Accepts XLSX and CSV files. Maximum file size:{" "}
                          {formatMegabytes(MAX_FILE_SIZE_BYTES)}.
                        </span>
                      </div>
                    )}
                  </Files>
                  {this.state.showErrors && this.state.message && (
                    <p className={s.uploadError} role="alert">
                      {this.state.message}
                    </p>
                  )}
                </div>
                <FormGroup>
                  <div
                    style={{
                      backgroundImage: `url('${this.state.fileUrl}'`,
                    }}
                    id="media"
                    className={s.preview}
                  >
                    {this.state.fileUpload && (
                      <Progress
                        className={s.mediaProgress}
                        value={this.calculateUploadProgress()}
                        animated={this.isUploading()}
                        striped={this.isUploading()}
                        color={this.isUploading() ? "default" : "success"}
                      />
                    )}
                  </div>
                </FormGroup>
              </div>
            )}
          </div>

          {this.state.currentStep !== "SelectFileType" &&
            this.renderStepFooter()}

          {this.renderDuplicatesModal()}
        </div>
      </main>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  hideSelection: () => {
    dispatch(commonActions.hideSelection());
  },
  refreshDashboard: () => {
    dispatch(commonActions.refreshDashboard());
  },
});

const mapStateToProps = state => {
  const { firebase, user } = state;

  const segments =
    isLoaded(firebase.data[user.currentCharity.id]) &&
    isLoaded(firebase.data[user.currentCharity.id].segments)
      ? !isEmpty(firebase.data[user.currentCharity.id].segments)
        ? get(firebase, `ordered.${user.currentCharity.id}.segments`)
            .map((request: { key: string; value: Segment }) => {
              const data: Segment = request.value;

              if (data.isKeyword) return null;

              return {
                value: data.name,
                label: data.name,
              };
            })
            .filter((segment: SelectOption | null) => segment != null)
        : []
      : null;

  return {
    email: user.email,
    name: user.name,
    firstName: user.firstName,
    lastName: user.lastName,
    photoUrl: user.photoUrl,
    currentCharity: user.currentCharity,
    userId: user.id,
    segmentsOptions: segments,
  };
};

export default serviceCaller(
  withLoginRedirect(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(
      firebaseConnect((_props, store) => {
        const charityId = store.getState().user.currentCharity.id;

        if (charityId) {
          return [
            {
              path: `${charityId}/segments`,
              queryParams: ["orderByChild=name"],
            },
          ];
        } else {
          return [];
        }
      })(withStyles(s, breadcrumbStyles)(ImportFile))
    )
  )
);
